import { incomeTaxYear } from "@stb/jurisdiction-au/income-tax"

import { displayYear, formatCheckedOn } from "../components/home/home-content"
import { formatAud, SimpleTable, SourceLine } from "../components/landing"
import { PayCalculator } from "../components/landing/pay-calculator"
import {
  answerSentence,
  DEFAULT_FORM,
  DEFAULT_FY,
  evaluate,
  SHOWN_PERIODS,
} from "../components/landing/pay-calculator-model"
import type { Guide } from "./guide-types"

/** Served at /pay-calculator (T914), not under /guides. Copy source: docs/design/html/flows/web-landing-tool-pay.mjs and docs/specs/pay-calculator-spec.md section 6. */
const year = incomeTaxYear(DEFAULT_FY)
const fy = displayYear(DEFAULT_FY)
const checkedOn = year.resident.source.checkedOn
const checked = formatCheckedOn(checkedOn)
const startYear = Number(DEFAULT_FY.slice(0, 4))
const previousFy = displayYear(`${startYear - 1}-${String(startYear).slice(2)}`)

const dollars = (n: number) => `A$${n.toLocaleString("en-AU")}`
const cents = (bp: number) => `${bp / 100}c`
const percent = (bp: number) => `${bp / 100}%`
const whole = (v: Parameters<typeof formatAud>[0]) => formatAud(v).replace(/\.00$/, "")
const standardDeduction = dollars(year.standardDeduction?.maxDollars ?? 0)

const example = evaluate(DEFAULT_FORM)
const exampleRows = example.kind === "ok" ? example.result.perPeriod : undefined
const exampleDeduction =
  example.kind === "ok" && example.result.annual.standardDeduction.amount > 0
    ? example.result.annual.standardDeduction
    : undefined

/** Hourly example: the engine works out the year and the tax, the page only picks the input. */
const HOURLY_RATE = 40
const hourly = evaluate({ ...DEFAULT_FORM, amount: String(HOURLY_RATE), period: "hour" })
const hourlyResult = hourly.kind === "ok" ? hourly.result : undefined

const brackets = year.resident.brackets
const bracketRows = brackets.map((b, i) => {
  const next = brackets[i + 1]
  const range = next
    ? `${dollars(i === 0 ? 0 : b.from + 1)} – ${dollars(next.from)}`
    : `${dollars(b.from + 1)} and over`
  const tax =
    b.rateBp === 0
      ? "Nil"
      : b.baseDollars === 0
        ? `${cents(b.rateBp)} for each A$1 over ${dollars(b.from)}`
        : `${dollars(b.baseDollars)} plus ${cents(b.rateBp)} for each A$1 over ${dollars(b.from)}`
  return { header: range, cells: [tax] }
})

const helpBands = year.studyLoan.bands
const helpRows = helpBands.map((b, i) => {
  const next = helpBands[i + 1]
  const range = next
    ? `${dollars(i === 0 ? 0 : b.from + 1)} – ${dollars(next.from)}`
    : `${dollars(b.from + 1)} and over`
  const repayment =
    b.rateBp === 0
      ? "Nil"
      : b.ofWholeIncome
        ? `${percent(b.rateBp)} of your whole repayment income`
        : b.baseDollars === 0
          ? `${percent(b.rateBp)} of income over ${dollars(b.from)}`
          : `${dollars(b.baseDollars)} plus ${percent(b.rateBp)} of income over ${dollars(b.from)}`
  return { header: range, cells: [repayment] }
})

const TAX_TABLES_URL = "https://www.ato.gov.au/tax-rates-and-codes/tax-tables-overview"

const PERIOD_ROWS = [
  ["Pay before tax", "gross"],
  ["Income tax", "incomeTaxAfterLito"],
  ["Medicare levy", "medicareLevy"],
  ["Take-home", "takeHome"],
  ["Employer super", "employerSuper"],
] as const

const periodLabel = { week: "Week", fortnight: "Fortnight", month: "Month", year: "Year" } as const

export const payGuide: Guide = {
  slug: "pay-calculator",
  title: `Pay Calculator Australia ${fy}: Take-Home Pay`,
  description: `Free pay calculator for Australia, ${fy}: take-home pay after tax, Medicare levy and HELP. Weekly, fortnightly, monthly or hourly, with ATO sources.`,
  crumb: "Pay calculator",
  h1: `Pay calculator: take-home pay after tax in Australia (${fy})`,
  published: "2026-10-30",
  reviewed: "2026-10-30",
  badge: "Free tool",
  financialYear: fy,
  checked,
  sources: [
    { label: "ATO, tax rates for Australian residents", href: year.resident.source.url },
    { label: "Medicare levy reduction", href: year.medicare.source.url },
  ],
  answer: answerSentence(DEFAULT_FORM, formatAud),
  answerMore: (
    <>
      The calculator works out take-home pay for employees in Australia after income tax, the
      Medicare levy and any HELP repayment, using ATO rates for {fy}. The rates are the same in
      every state. Enter your own pay below.
    </>
  ),
  related: [
    {
      label: "Sole trader tax calculator: how much ABN income to set aside",
      href: "/sole-trader-tax-calculator",
    },
    { label: "GST calculator: add GST to a price or take it out", href: "/gst-calculator" },
    { label: "Cents per km calculator: your car expense claim", href: "/cents-per-km-calculator" },
    { label: "ATO logbook requirements: what to record", href: "/guides/ato-logbook-requirements" },
    { label: "Free invoice generator: make a tax invoice", href: "/invoice-generator" },
    { label: "Invoicing for sole traders: send invoices, see who has paid", href: "/invoicing" },
  ],
  toc: [
    { id: "calculator", label: "Work out your pay" },
    { id: "periods", label: "Weekly, fortnightly, monthly" },
    { id: "hourly", label: "From an hourly rate" },
    { id: "payg", label: "PAYG withholding" },
    { id: "rates", label: `Tax rates ${fy}` },
    { id: "help", label: "HELP repayments" },
    { id: "faq", label: "Common questions" },
  ],
  cta: {
    title: "Earning on an ABN too? Keep the books in one place",
    description:
      "ABN income usually has no tax taken out, so you set it aside yourself. Kestly keeps your invoices, receipts and trips ready for tax time.",
    ctaLabel: "Start free",
    ctaHref: "/login?from=pay-calculator",
  },
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-3 md:gap-4">
        <h2 id="calculator" className="text-2xl font-semibold">
          Work out your take-home pay
        </h2>
        {/* Mode nav hidden while Employee is the only mode (T1273): a single link-less item is an empty landmark.
            Restore the "Calculator mode" nav when a second mode exists. */}
        <PayCalculator />
      </section>

      {exampleRows && (
        <section aria-labelledby="periods" className="grid gap-3">
          <h2 id="periods" className="text-2xl font-semibold">
            Weekly, fortnightly and monthly take-home pay
          </h2>
          <p className="max-w-[70ch]">
            A {whole(exampleRows.year.gross)} salary for a resident in {fy}, super not included
            {exampleDeduction && ` and the ${whole(exampleDeduction)} standard deduction applied`}.
            Each column is the year divided by 52, 26 or 12.
          </p>
          <div className="hidden md:block">
            <SimpleTable
              caption={`Take-home pay on ${whole(exampleRows.year.gross)} by pay period, ${fy}`}
              columns={["", ...SHOWN_PERIODS.map((p) => periodLabel[p])]}
              rows={PERIOD_ROWS.map(([header, key]) => ({
                header,
                cells: SHOWN_PERIODS.map((p) => formatAud(exampleRows[p][key])),
              }))}
            />
          </div>
          <div className="grid gap-3 md:hidden">
            {SHOWN_PERIODS.map((p) => (
              <dl
                key={p}
                className="bg-card border-border grid gap-1 rounded-lg border p-3 text-sm"
              >
                <dt className="font-semibold">{periodLabel[p]}</dt>
                {PERIOD_ROWS.map(([label, key]) => (
                  <dd key={key} className="flex justify-between">
                    <span>{label}</span>
                    <span className="tabular-nums">{formatAud(exampleRows[p][key])}</span>
                  </dd>
                ))}
              </dl>
            ))}
          </div>
        </section>
      )}

      {hourlyResult && (
        <section aria-labelledby="hourly" className="grid gap-3">
          <h2 id="hourly" className="text-2xl font-semibold">
            From an hourly rate to a yearly salary
          </h2>
          <p className="max-w-[70ch]">
            Choose Hour under Paid per and enter your hours a week. The calculator multiplies your
            rate by your hours and by 52 weeks, then works out the tax on that yearly amount.
          </p>
          <p className="max-w-[70ch]">
            Example: A${HOURLY_RATE} an hour for {DEFAULT_FORM.hoursPerWeek} hours a week is{" "}
            {whole(hourlyResult.annual.gross)} a year before tax. That is about{" "}
            {formatAud(hourlyResult.perPeriod.week.takeHome)} a week after tax in {fy}.
          </p>
        </section>
      )}

      <section aria-labelledby="payg" className="grid gap-3">
        <h2 id="payg" className="text-2xl font-semibold">
          PAYG withholding and the tax you pay for the year
        </h2>
        <p className="max-w-[70ch]">
          Your employer takes tax out of each pay using the ATO tax tables. This is PAYG
          withholding. The calculator works out your tax for the whole year and divides it by your
          pays, so your payslip can differ by a few dollars.
        </p>
        <p className="max-w-[70ch]">
          Any difference between what your employer withholds and your tax for the year is settled
          when you lodge your return. If you include the standard deduction, it comes off at that
          point too, so your employer may withhold a little more each pay.
        </p>
        <SourceLine
          sources={[{ label: "ATO tax tables", href: TAX_TABLES_URL }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="rates" className="grid gap-3">
        <h2 id="rates" className="text-2xl font-semibold">
          Income tax rates {fy}: the same in every state
        </h2>
        <p className="max-w-[70ch]">
          Income tax is federal, so these rates apply in NSW, VIC, QLD, WA, SA, TAS, the ACT and the
          NT. Most people also pay the {percent(year.medicare.rateBp)} Medicare levy; people on low
          incomes pay less or none.
        </p>
        <SimpleTable
          caption={`Resident income tax rates ${fy}, not including the Medicare levy`}
          columns={["Taxable income", `Tax on this income (${fy})`]}
          rows={bracketRows}
        />
        <SourceLine
          sources={[{ label: "ATO", href: year.resident.source.url }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="help" className="grid gap-3">
        <h2 id="help" className="text-2xl font-semibold">
          HELP and study loan repayments {fy}
        </h2>
        <p className="max-w-[70ch]">
          Tick the study loan box under More options to add the compulsory repayment. In the top
          band the rate applies to your whole income, not only the part over the threshold.
        </p>
        <SimpleTable
          caption={`HELP and other study loan repayment rates ${fy}`}
          columns={["Repayment income", `Repayment (${fy})`]}
          rows={helpRows}
        />
        <p className="text-muted-foreground max-w-[70ch] text-sm">
          The ATO counts more than taxable income, for example reportable fringe benefits and
          reportable super contributions. The calculator uses taxable income only.
        </p>
        <SourceLine
          sources={[{ label: "ATO study loan repayment rates", href: year.studyLoan.source.url }]}
          financialYear={fy}
          checked={formatCheckedOn(year.studyLoan.source.checkedOn)}
        />
      </section>
    </>
  ),
  faq: [
    {
      question: "Is take-home pay different in NSW, VIC, QLD or WA?",
      answer:
        "No. Income tax, the Medicare levy and HELP repayments are federal, so the same salary gives the same take-home pay in NSW, VIC, QLD, WA, SA, TAS, the ACT and the NT.",
    },
    {
      question: "Why is my second job taxed more?",
      answer:
        "You can claim the tax-free threshold from one employer only. Your other job is taxed from the first dollar, so enter your main job's income under More options to see what the second job adds.",
    },
    {
      question: "Why are there 26 fortnights in a year?",
      answer:
        "A fortnight is two weeks and a year has 52 weeks, so there are 26 fortnights. The calculator always divides the year by 26, even in a year with 27 paydays.",
    },
    {
      question: "Is employer super part of my take-home pay?",
      answer: `No. Your employer pays ${percent(year.superGuarantee.rateBp)} super guarantee into your fund on top of your salary, so it is shown separately. If your package includes super, choose Included and the calculator splits it out first.`,
    },
    {
      question: `What is the ${standardDeduction} standard deduction?`,
      answer: `It is law (Treasury Laws Amendment (Tax Reform No. 1) Act 2026, Schedule 4). From ${fy}, Australian residents with wages, salary and similar pay with tax withheld get a deduction of up to ${standardDeduction} in their tax return, which the ATO applies automatically. It is the smaller of ${standardDeduction} and that pay, less any work expenses it covers that you claim. It does not apply to business income, so a sole trader's business profit does not count. We leave it off by default because it depends on your own situation; tick it under More options to include it.`,
    },
    {
      question: "What does the Medicare levy low-income reduction do?",
      answer: `The levy is ${percent(year.medicare.rateBp)} of taxable income. A single person on up to ${dollars(year.medicare.singleLower)} pays none, and up to ${dollars(year.medicare.singleUpper)} pays a reduced amount.${year.medicare.status === "provisional" ? ` The ATO has not yet published the ${fy} thresholds, so the calculator uses the ${previousFy} values.` : ""} Family thresholds are not applied. Some people are exempt from the levy, for example with a medical exemption or when not entitled to Medicare benefits, and foreign residents pay none. Untick Include the Medicare levy under More options if that is you. The Medicare levy surcharge is separate and not included.`,
    },
    {
      question: "How are foreign residents and working holiday makers taxed?",
      answer: `Foreign residents pay tax from the first dollar at different rates and no Medicare levy. Working holiday makers have their own rates. Change Tax residency under More options.${year.foreignResident.status === "provisional" ? ` The ATO has not yet published the ${fy} rates for these groups, so the calculator uses ${previousFy} rates and marks them provisional.` : ""}`,
    },
    {
      question: "I work on an ABN. Does this calculator fit my income?",
      answer: (
        <>
          Not this one. It assumes your employer takes tax out of each pay and adds super. ABN
          income usually has neither, so you set tax aside yourself. Use the{" "}
          <a
            href="/sole-trader-tax-calculator"
            className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            sole trader tax calculator
          </a>{" "}
          to see how much to put aside.
        </>
      ),
    },
    {
      question: "Is this tax advice?",
      answer: `No. It is an estimate from the ATO rates for ${fy} and the assumptions listed under the result. For your own situation, check with the ATO or a registered tax agent.`,
    },
  ],
}
